import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { getHome } from '../lib/api'
import { localIsoDay } from '../lib/format'
import { browserTimeZone, isOldHomeApi, readHomeSummary, type HomeSummaryState } from '../lib/home'
import { PENDING_JOB_EVENT, PENDING_JOB_PREFIX } from '../lib/pendingJob'
import { useResource } from '../lib/useResource'
import { useAuth } from './AuthContext'

/**
 * The shell's ONE read of `/home` (Sprint 1.9.9 P6, R55).
 *
 * ── WHY THE SHELL, AND NOT HOME ─────────────────────────────────────────────
 *
 * Home's mirror is built from it, and the nav's State entry (S6) exists only
 * when the reading carries a `state_map` — so the nav needs the answer on every
 * route, and a read per route would be a read per click. Mounted inside
 * `ConsoleRoutes`, under `RequireAuth`, so a gated account never makes it
 * (`gatedAccount.test.tsx` "calls /me and NOTHING else").
 *
 * ── WHEN IT READS AGAIN ─────────────────────────────────────────────────────
 *
 * Nothing polls. It reads once for the console's life, and again only when:
 *  - the pending-job pointer is written or cleared in this tab
 *    (`PENDING_JOB_EVENT`) — which is exactly "a recording started" and "a
 *    recording finished", announced by the two pages that own the pointer;
 *  - another tab changes a pending-job key, or clears storage (`key: null`);
 *  - a "Try again" calls `reload()`;
 *  - `refreshIfStale()` finds the LOCAL day has changed since the last read. A
 *    no-op until a read has succeeded (critique #7), so a mount during the first
 *    read never aborts and re-issues it; and once per stale day, so two calls
 *    in a row reload once. Both sides of the comparison are the page's own
 *    clock, never the server's `today`, so a server that fell back to UTC cannot
 *    start a reload loop.
 *
 * During the old-API interval (1.9.9 D33) those events re-probe `/home` too,
 * which is intended (R55).
 */

export interface HomeSummaryValue {
  state: HomeSummaryState
  reload: () => void
  refreshIfStale: () => void
}

const HomeSummaryContext = createContext<HomeSummaryValue | null>(null)

/**
 * Outside the provider — a unit test that mounts Home alone — the answer is the
 * old API's, so the 1.9.6 Home renders exactly as it did. `homeProvider.test.tsx`
 * exists because this fallback would otherwise hide a provider that was never
 * mounted at all.
 */
export const NO_PROVIDER: HomeSummaryValue = {
  state: { kind: 'absent' },
  reload: () => {},
  refreshIfStale: () => {},
}

interface Fetched {
  body: unknown
  /** The local calendar day this answer was read on. */
  fetchedOn: string
}

export function HomeSummaryProvider({ children }: { children: ReactNode }) {
  const { account } = useAuth()
  const userId = account?.user_id ?? null
  // Read once: the zone decides what `today` is on the server, and a summary
  // whose "today" moved under a mounted screen would be a different summary.
  const [timezone] = useState(browserTimeZone)

  const load = useCallback(
    (signal: AbortSignal): Promise<Fetched | null> =>
      userId === null
        ? Promise.resolve(null)
        : getHome(userId, { condition: 'EC', timezone }, { signal }).then((body) => ({
          body,
          fetchedOn: localIsoDay(new Date()),
        })),
    [userId, timezone],
  )
  const home = useResource(load)
  const { reload } = home

  useEffect(() => {
    const onPointer = () => reload()
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key.startsWith(PENDING_JOB_PREFIX)) reload()
    }
    window.addEventListener(PENDING_JOB_EVENT, onPointer)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(PENDING_JOB_EVENT, onPointer)
      window.removeEventListener('storage', onStorage)
    }
  }, [reload])

  const fetchedOn = home.data?.fetchedOn ?? null
  // The day a refresh was last asked FOR. `undefined` rather than `null`, so it
  // can never stand in for the null check above it.
  const refreshedFor = useRef<string | undefined>(undefined)
  const refreshIfStale = useCallback(() => {
    if (fetchedOn === null) return
    if (fetchedOn === localIsoDay(new Date())) return
    if (refreshedFor.current === fetchedOn) return
    refreshedFor.current = fetchedOn
    reload()
  }, [fetchedOn, reload])

  const state = useMemo(() => toState(home.data, home.error), [home.data, home.error])
  const value = useMemo(() => ({ state, reload, refreshIfStale }), [state, reload, refreshIfStale])
  return <HomeSummaryContext value={value}>{children}</HomeSummaryContext>
}

/**
 * What the read has become (1.9.9 D33).
 *
 * A background read that fails keeps the last summary: `useResource` keeps
 * `data` and sets `error`, and the mirror stays with a notice beside it rather
 * than being swapped for the old Home. A 404 is the old API only when its code
 * is `not_found` — a proxy's bodiless 404 is a failure, not an old server.
 */
function toState(data: Fetched | null, error: unknown): HomeSummaryState {
  const read = data === null ? null : readHomeSummary(data.body)
  if (error != null) {
    if (isOldHomeApi(error)) return { kind: 'absent' }
    if (read !== null && read.readable) return { kind: 'ready', summary: read.summary, reloadError: error }
    return { kind: 'failed', error, detail: null }
  }
  if (read === null) return { kind: 'loading' }
  return read.readable
    ? { kind: 'ready', summary: read.summary, reloadError: null }
    : { kind: 'failed', error: null, detail: read.detail }
}

export function useHomeSummary(): HomeSummaryValue {
  return useContext(HomeSummaryContext) ?? NO_PROVIDER
}
